import React, { useState, useEffect } from 'react';
import { ActivityMenuProps } from './types';
import { LoadingSpinner } from '@tw/tw-component-library';
import { ActivityItem } from './Components/ActivityItem';
import { EmailPreview } from './Components/EmailPreview';
import {
  ActivityList,
  LoadingMoreIndicator,
  ViewHeader,
  ViewTitle,
  CloseButton,
  EmptyState,
  FullPageContainer,
  SidePopoutContainer,
  ActivityMenuContainer,
} from './styles/styled';

export const ActivityMenu: React.FC<ActivityMenuProps> = ({
  activities,
  onActivityClick,
  isLoading = false,
  maxHeight,
  emptyStateMessage = 'No activities to show',
  view = 'default',
  onClose,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<{
    subject: string;
    html: string;
    to: string;
  } | null>(null);

  const isInfiniteScroll = maxHeight === 'endless' || maxHeight === 'infinite';

  const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    if (!isInfiniteScroll || !onLoadMore || !hasMore || isLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const threshold = 100;

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      await onLoadMore();
    }
  };

  const handleClose = () => {
    if (view === 'sidePopout') {
      setIsClosing(true);
      setTimeout(() => {
        onClose?.();
        setIsClosing(false);
      }, 300);
    } else {
      onClose?.();
    }
  };

  const renderActivityList = () => (
    <ActivityList
      $maxHeight={view === 'default' ? maxHeight : undefined}
      $isInfinite={isInfiniteScroll}
      $view={view}
      onScroll={view === 'fullPage' ? undefined : handleScroll}
    >
      {activities.map((activity, index) => (
        <ActivityItem
          key={`${activity.id}-${activity.timestamp}-${index}`}
          activity={activity}
          onClick={() => onActivityClick?.(activity)}
          onEmailPreview={(emailContent) => setSelectedEmail(emailContent)}
        />
      ))}

      {isLoadingMore && (
        <LoadingMoreIndicator>
          <LoadingSpinner size="small" />
          <div>Loading more activities...</div>
        </LoadingMoreIndicator>
      )}

      {!hasMore && activities.length > 0 && !isLoadingMore && (
        <LoadingMoreIndicator>
          <div>No more activities to load</div>
        </LoadingMoreIndicator>
      )}
    </ActivityList>
  );

  const renderContent = () => (
    <>
      {(view === 'fullPage' || view === 'sidePopout') && (
        <ViewHeader>
          <ViewTitle>Activity History</ViewTitle>
          {view === 'sidePopout' && (
            <CloseButton onClick={handleClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </CloseButton>
          )}
        </ViewHeader>
      )}

      {isLoading ? (
        <EmptyState>
          <LoadingSpinner size="medium" />
        </EmptyState>
      ) : !activities.length ? (
        <EmptyState>{emptyStateMessage}</EmptyState>
      ) : (
        renderActivityList()
      )}
    </>
  );

  useEffect(() => {
    if (
      view !== 'fullPage' ||
      !isInfiniteScroll ||
      !onLoadMore ||
      !hasMore ||
      isLoadingMore
    )
      return;

    const handleWindowScroll = async () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = 100;
      if (document.body.offsetHeight - scrollPosition < threshold) {
        await onLoadMore();
      }
    };

    window.addEventListener('scroll', handleWindowScroll);
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [view, isInfiniteScroll, onLoadMore, hasMore, isLoadingMore]);

  return (
    <>
      {view === 'fullPage' ? (
        <FullPageContainer>{renderContent()}</FullPageContainer>
      ) : view === 'sidePopout' ? (
        <SidePopoutContainer className={isClosing ? 'closing' : ''}>
          {renderContent()}
        </SidePopoutContainer>
      ) : (
        <ActivityMenuContainer>{renderContent()}</ActivityMenuContainer>
      )}
      <EmailPreview
        emailContent={selectedEmail}
        onClose={() => setSelectedEmail(null)}
      />
    </>
  );
};
